import React, { FC } from 'react';
import { usePagination } from '../../hooks/usePagination';
import { PageLink } from './PageLink/PageLink';

import s from './Pagination.scss';
import { useIsMobile } from '../../hooks/useIsMobile';

export const Pagination: FC = () => {
  const { totalPages, currentPage } = usePagination();
  const isMobile = useIsMobile();

  if (totalPages <= 1) {
    return null;
  }

  const firstPage = 1;
  const firstMiddlePage = Math.max(
    Math.min(totalPages - 5, currentPage - 2),
    2,
  );
  const lastMiddlePage = Math.min(firstMiddlePage + 4, totalPages - 1);
  const lastPage = totalPages;

  const calculateMobileMiddlePage = () => {
    if (currentPage === firstPage) {
      return 2;
    } else if (currentPage === lastPage) {
      return lastPage - 1;
    }
    return currentPage;
  };

  const mobileMiddlePage =
    isMobile && totalPages > 2 ? calculateMobileMiddlePage() : -1;

  const isShowLeftEllipsis = () => {
    return (isMobile ? mobileMiddlePage : firstMiddlePage) - firstPage > 1;
  };

  const isShowRightEllipsis = () => {
    if (isMobile) {
      return mobileMiddlePage > 1 ? lastPage - mobileMiddlePage > 1 : false;
    }

    return lastPage - lastMiddlePage > 1;
  };

  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;

  return (
    <nav className={s.pagination}>
      <ul className={s.list}>
        {hasPrev && (
          <li className={s.prev}>
            <PageLink page={currentPage - 1} isPrev>
              ←
            </PageLink>
          </li>
        )}
        <li>
          <PageLink page={firstPage}>{firstPage}</PageLink>
        </li>
        {isShowLeftEllipsis() && <div className={s.ellipsis}>&hellip;</div>}
        {!isMobile
          ? Array.from(
              { length: lastMiddlePage - firstMiddlePage + 1 },
              (_, i) => i + firstMiddlePage,
            ).map((page) => (
              <li key={page}>
                <PageLink page={page}>{page}</PageLink>
              </li>
            ))
          : mobileMiddlePage > 1 && (
              <li>
                <PageLink page={mobileMiddlePage}>{mobileMiddlePage}</PageLink>
              </li>
            )}
        {isShowRightEllipsis() && <div className={s.ellipsis}>&hellip;</div>}
        <li>
          <PageLink page={lastPage}>{lastPage}</PageLink>
        </li>
        {hasNext && (
          <li className={s.next}>
            <PageLink page={currentPage + 1} isNext>
              →
            </PageLink>
          </li>
        )}
      </ul>
    </nav>
  );
};
